import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";
import { videoSchema } from "@/lib/video-schema";

const SLUG = "/skylight-repair";
const NAME = "Skylight Repair";
const TITLE = "Skylight Repair & Replacement in Phoenix | Durafoam";
const DESCRIPTION =
  "Skylight repair and replacement in Phoenix — leaks, broken domes, fogged glass, gasket and flashing failures. Free inspection. Family-owned since 1989.";

const faqs = [
  { q: "Is my skylight or my roof leaking?", a: "Either is possible and they're easy to confuse — water can run down the curb of a skylight and onto the roof, or roof leaks above the skylight can drip down its frame. We trace the water path on the roof to identify the actual source." },
  { q: "Can my skylight be repaired or does it need replacing?", a: "Most skylight leaks are flashing or gasket failures, both repairable. Cracked or fogged glazing usually means replacement. We'll show you what we find before recommending anything." },
  { q: "How long do skylights last in Phoenix?", a: "Quality units typically 20–25 years. The dome material yellows under UV faster here than in milder climates; gaskets dry out at similar rates to pipe boots — 7–10 years for the rubber components." },
  { q: "Do you install solar-powered skylights?", a: "Yes — we install Velux solar-powered skylights with built-in remote-controlled solar shades. They're designed as a direct swap for existing dome skylights, so most installs need no roof patching or framing changes. We also repair and reseal solar tube curbs and flashings on existing units." },
  { q: "Are solar tubes better than skylights?", a: "It depends on the room and what you want. Solar tubes (also called sun tunnels) cost less, fit tight spaces like hallways and bathrooms, and pipe daylight in without opening the ceiling to the sky. A real skylight gives you the open-sky view and, with a venting or solar-powered unit, airflow and remote-controlled shades. We measure the opening and recommend the right option for the space — not the one that's easiest to sell." },
  { q: "Do solar skylights qualify for a tax credit?", a: "Velux solar-powered skylights may qualify for the federal residential clean energy tax credit because they run on solar power rather than household electricity. Eligibility and credit amounts change, so we confirm current eligibility at your inspection and hand over the manufacturer documentation you need to claim it." },
  { q: "Will you match my existing skylight if it needs replacement?", a: "Yes — we match Velux, Sun-Tek, and other common brands by size and profile. If the original model is discontinued, we'll specify the closest current equivalent." },
];

function YouTubeShort({ id, title }: { id: string; title: string }) {
  return (
    <div className="mx-auto w-full max-w-[360px]">
      <div className="relative w-full overflow-hidden rounded-xl bg-black shadow-lg" style={{ aspectRatio: "9 / 16" }}>
        <iframe
          src={`https://www.youtube.com/embed/${id}`}
          title={title}
          loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          className="absolute inset-0 h-full w-full"
        />
      </div>
    </div>
  );
}

export const Route = createFileRoute("/skylight-repair")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) => {
    const base = serviceHead({ override: loaderData?.seo, slug: SLUG, title: TITLE, description: DESCRIPTION, serviceName: NAME, faqs, rating: loaderData?.rating, reviewCount: loaderData?.reviewCount });
    return {
      ...base,
      scripts: [
        ...(base.scripts ?? []),
        {
          type: "application/ld+json",
          children: JSON.stringify(
            videoSchema({
              name: "Velux Solar-Powered Skylight Installation in Phoenix",
              description:
                "Durafoam Roofing installs Velux solar-powered skylights with built-in remote-controlled shades — a quick-swap replacement for existing skylight domes with no roof patching required.",
              youtubeId: "UxrnD7XQWeQ",
              uploadDate: "2026-06-25T12:00:00-07:00",
            }),
          ),
        },
      ],
    };
  },
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="skylight-repair"
      eyebrow="Skylights"
      h1="Skylight Repair & Replacement in Phoenix"
      subhead="Skylight leaks, broken domes, fogged glass, gasket and flashing failures — fixed by roofers who keep skylight curbs dry through monsoon season. Free inspection."
      heroBullets={["Skylight leak repair", "Gasket and weatherstrip replacement", "Full skylight replacement", "Solar tube repairs"]}
      whatIs={{
        heading: "Where skylight leaks actually start",
        body: (
          <>
            <p>
              Most "skylight leaks" aren't leaks through the skylight itself — they're failures of
              the flashing that ties the skylight curb into the surrounding roof. The curb is the
              raised wooden or metal frame that the skylight sits on; the flashings and counterflashings
              wrap around that curb and onto the roof surface. When those flashings dry, crack, or
              shift, water gets behind them and tracks down the inside of the curb until it drips
              into the room below.
            </p>
            <p>
              The other common source is gasket failure where the glazing meets the frame. Phoenix
              heat dries and shrinks those gaskets in 7–10 years. Once they fail, the unit can leak
              even when the flashings around it are perfect.
            </p>
            <p>
              We diagnose which mode is failing, then fix it correctly — curb flashings rebuilt,
              gaskets replaced, or the unit swapped out entirely if the glazing or frame is shot.
              A broken skylight (cracked dome, fogged glass, failed seal) usually means replacement;
              a water stain below it often just means the flashing needs resealing.
            </p>
            <p>
              Wondering what it costs? See our{" "}
              <Link to="/skylight-repair-cost-phoenix" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">
                skylight repair cost guide
              </Link>{" "}
              for Phoenix pricing on reseals, glass replacement, and full skylight swaps — most
              flashing repairs run $350–$750.
            </p>
          </>
        ),
      }}
      climate={{
        heading: "Why skylights struggle in the Phoenix sun",
        body: (
          <>
            <p>
              Skylights take more abuse here than almost anywhere else. They face the sun directly
              and don't have the shading a wall might provide. That accelerates every failure mode
              the unit has.
            </p>
            <ul>
              <li><strong>UV degradation:</strong> Plastic domes yellow and embrittle; acrylic glazing loses optical clarity.</li>
              <li><strong>Gasket shrinkage:</strong> Heat dries rubber gaskets at the glazing perimeter, opening up the most common leak path.</li>
              <li><strong>Thermal flex:</strong> The curb expands and contracts more than the surrounding roof, working flashings loose over time.</li>
              <li><strong>Condensation confusion:</strong> Heavy AC use creates condensation on the inside of skylights that homeowners often mistake for a roof leak.</li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our skylight repair process",
        steps: [
          { title: "Diagnose the leak path", body: "We trace where water is actually entering — flashing, gasket, glazing, or condensation — before recommending a fix." },
          { title: "Recommend repair vs. replace", body: "Flashing/gasket work if the unit is sound. Full replacement if the glazing or frame is compromised. Honest call either way." },
          { title: "Reseal or swap the unit", body: "Curb flashings rebuilt and counterflashings reset, or the unit is removed and the new one set with fresh flashings." },
          { title: "Verify and warranty", body: "Water-tested where appropriate and backed by a written workmanship warranty." },
        ],
      }}
      faqs={faqs}
      extraSection={{
        heading: "New Velux solar-powered skylights with built-in sun shade",
        body: (
          <>
            <p>
              The newest skylight we install is the <strong>Velux solar-powered skylight</strong> —
              a self-charging unit with a <strong>built-in solar shade</strong> and a <strong>remote
              control</strong> that lets you open, close, and shade the skylight from anywhere in the
              room. No electrical wiring, no electrician required — the solar panel on the unit
              charges an internal battery that powers the motor.
            </p>
            <ul>
              <li><strong>Built-in solar shade:</strong> A pull-down shade is integrated into the frame. Use the remote to close it and block the afternoon sun, or open it for full daylight — no separate shade installation needed.</li>
              <li><strong>Remote control operation:</strong> Open, close, and position the shade with a handheld remote. Some models also pair with a smart-home hub for app or voice control.</li>
              <li><strong>Solar-powered, no wiring:</strong> The unit charges itself from sunlight, so there's no need to run electrical to the roof or hire an electrician.</li>
              <li><strong>Rain sensor auto-close:</strong> If a monsoon blows in while you're away, the skylight senses rain and closes itself automatically.</li>
              <li><strong>Direct swap for old domes:</strong> Designed to fit standard existing skylight openings — most installs are a clean pull-and-replace with no roof patching or framing changes.</li>
            </ul>
            <p className="text-sm text-foreground/70">See a Velux solar-powered skylight install on a Phoenix home:</p>
            <YouTubeShort id="UxrnD7XQWeQ" title="Velux solar-powered skylight install" />
            <h3 className="pt-2 text-xl font-semibold text-foreground">Solar tubes & sun tunnels</h3>
            <p>
              For hallways, bathrooms, closets, and other tight spaces where a full skylight won't
              fit, a <strong>solar tube</strong> (also called a sun tunnel) pipes daylight in through
              a small rooftop dome and reflective tube. It's the most affordable daylight option and
              requires the least roof work. We install and repair solar tubes as well.
            </p>
            <h3 className="pt-2 text-xl font-semibold text-foreground">What does a solar skylight or solar tube cost?</h3>
            <p>
              Honestly, it depends on what we find on the roof. A <strong>simple pull-off-the-old-unit
              and drop-in swap</strong> to a new Velux solar skylight is the lower-cost path — the
              opening is already there and the curb is sound. The price goes up when the job needs a
              curb rebuild, re-tying the flashing back into the existing roof, or new framing. A
              solar tube is usually the most affordable option because it's a smaller opening and
              less roof work.
            </p>
            <p>
              Rather than quote a number blind, we come out and <strong>inspect the opening for
              free</strong> — then give you exact pricing and lay out the best skylight or solar tube
              options for that room. If cost is the blocker on a larger job, ask us about{" "}
              <Link to="/financing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">payment options</Link>.
            </p>
          </>
        ),
      }}
      ctaLabel="Schedule a Skylight Inspection"
      ctaTitle="Stain under your skylight?"
      ctaBody="We'll diagnose whether the skylight, the flashing, or the surrounding roof is the actual problem — and give you a written estimate."
    />
  );
}
